import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import {
  rescueChipClaimEligibilityQuery,
  type RescueChipClaimEligibilityData,
  type RescueChipClaimStatus,
} from '../lib/rescueQueries';

/**
 * Per-chip eligibility state for the import wizard's claim flow:
 *   - `in_my_inventory` — chip is already registered to this rescue and
 *                         not yet attached to a pet; the import mutation
 *                         will register a new pet against it.
 *   - `claim`           — chip is unclaimed (no reseller) or unknown to
 *                         the system; the import mutation will claim it
 *                         for this rescue.
 *   - `other_reseller`  — chip is already owned by a different reseller;
 *                         the row must be rejected.
 *   - `already_linked`  — chip exists (in our inventory or elsewhere) and
 *                         is already attached to a pet; re-linking would
 *                         detach the existing registration so the backend
 *                         rejects, and we surface it at upload time
 *                         instead of waiting for the publish round trip.
 *   - `not_usable`      — chip exists but its condition (recalled,
 *                         damaged, decommissioned, etc.) prevents import.
 */
export type ClaimEligibility = 'in_my_inventory' | 'claim' | 'other_reseller' | 'already_linked' | 'not_usable';

interface UseChipClaimEligibilityResult {
  /** serial (uppercased) → eligibility. Missing entries are still in flight. */
  results: ReadonlyMap<string, ClaimEligibility>;
  /**
   * True while any of the requested serials hasn't been resolved yet.
   * Derived from results coverage rather than a separate state flag, so
   * there's no race window where `loading=false` and `results` is empty
   * before the fetch has fired. Forced to `false` when `error` is set so
   * the UI can stop showing a spinner and present the retry path.
   */
  loading: boolean;
  /**
   * True when the lookup failed (network / GraphQL). Surfaced so the
   * caller can show a warning + retry button rather than silently
   * treating every serial as claimable.
   */
  error: boolean;
  /** Re-runs the lookup for the current serial set. No-op if `skip` is true. */
  refetch: () => void;
}

/**
 * Maps the backend's six-way classification down to the five buckets
 * the import wizard cares about. `CLAIMABLE` and `NOT_FOUND` both flow
 * through the claim path — either the chip exists with no reseller
 * (CLAIMABLE) or it's not yet in the system and will be created on the
 * fly (NOT_FOUND). `OTHER_RESELLER`, `LINKED_TO_PET`, and `NOT_USABLE`
 * are all upload-time rejections that the backend's apply phase would
 * also raise.
 */
function eligibilityFor(status: RescueChipClaimStatus): ClaimEligibility {
  switch (status) {
    case 'IN_MY_INVENTORY':
      return 'in_my_inventory';
    case 'OTHER_RESELLER':
      return 'other_reseller';
    case 'LINKED_TO_PET':
      return 'already_linked';
    case 'NOT_USABLE':
      return 'not_usable';
    case 'CLAIMABLE':
    case 'NOT_FOUND':
      return 'claim';
  }
}

/**
 * Resolves whether each of the given chip serials can be claimed by this
 * rescue during a CSV import. Backed by the bulk
 * `rescueChipClaimEligibility` query — one round trip per upload, max
 * 500 serials per call (matches the wizard's row cap).
 *
 * On lookup failure, surfaces `error: true` and leaves `results` empty.
 * Callers should gate their submit path on `!error` (and offer a retry)
 * rather than letting a partially-verified batch publish — the backend
 * re-validates per row so a bad row WILL be rejected, but the user
 * deserves to know upload-time verification didn't complete instead of
 * seeing rows render as "ready" and then fail mid-publish.
 *
 * ## Cache policy: `no-cache`
 *
 * Eligibility state is mutable from the same session that's looking
 * it up — a successful publish turns every `claim`/`in_my_inventory`
 * serial into `linked_to_pet`. A cached result from before the publish
 * would then render the same serials as "ready to publish" on a
 * re-upload, when in reality they're no longer claimable. `no-cache`
 * skips both read and write so every upload sees the current backend
 * state, and the query never pollutes other consumers' cache views.
 */
export default function useChipClaimEligibility(
  serials: readonly string[],
  skip: boolean,
): UseChipClaimEligibilityResult {
  const apollo = useApolloClient();
  const [results, setResults] = useState<ReadonlyMap<string, ClaimEligibility>>(new Map());
  const [error, setError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);

  // Stable join key for the effect deps. Content-stable so upstream
  // memoization churn (new array, same serials) doesn't re-fire the
  // query. Uppercased + sorted matches the backend's normalization and
  // also normalizes Apollo's query-variable cache key.
  const uppercasedSerials = useMemo(() => serials.map((s) => s.toUpperCase()), [serials]);
  const serialsKey = useMemo(() => [...uppercasedSerials].sort().join('|'), [uppercasedSerials]);

  // Reset when `skip` flips. The effect below covers content changes via
  // `serialsKey`, but `skip` can flip independently and both `results`
  // and `error` should drop stale data on a close→reopen cycle.
  useEffect(() => {
    if (skip) {
      setResults(new Map());
      setError(false);
    }
  }, [skip]);

  useEffect(() => {
    if (skip || uppercasedSerials.length === 0) {
      return;
    }

    let cancelled = false;
    setError(false);

    (async () => {
      // Sorted variables so two permutations of the same serial set
      // collapse into one server call — irrelevant to the cache (we
      // bypass it), but it dedupes the network round-trip in the
      // rare case the deps re-fire with a reordered list.
      const sortedSerials = [...uppercasedSerials].sort();
      try {
        const { data } = await apollo.query<RescueChipClaimEligibilityData>({
          query: rescueChipClaimEligibilityQuery,
          variables: { serialNumbers: sortedSerials },
          // See "Cache policy" in the JSDoc — eligibility state goes
          // stale the moment a publish lands, so every lookup must
          // hit the network.
          fetchPolicy: 'no-cache',
        });
        if (cancelled) {
          return;
        }
        const next = new Map<string, ClaimEligibility>();
        for (const entry of data.rescueChipClaimEligibility) {
          // Backend normalizes to uppercase; defense-in-depth in case
          // that contract ever loosens.
          next.set(entry.serialNumber.toUpperCase(), eligibilityFor(entry.status));
        }
        // Defensive: if the backend skipped any serial we asked about
        // (shouldn't happen — the schema returns one entry per input),
        // mark it claim so the row isn't stuck "verifying" forever.
        // Backend re-validates atomically at import time, so an
        // optimistic skip here can't escape a bad claim.
        for (const serial of sortedSerials) {
          if (!next.has(serial)) {
            next.set(serial, 'claim');
          }
        }
        setResults(next);
      } catch {
        // Network / GraphQL failure: surface so the caller can show a
        // retry path and gate Publish. We intentionally do NOT optimistic-
        // fallback to `claim` — that previously let users submit rows
        // that the backend would reject mid-publish, with no indication
        // upload-time verification ever failed.
        if (cancelled) {
          return;
        }
        setResults(new Map());
        setError(true);
      }
    })();

    return () => {
      cancelled = true;
    };
    // `uppercasedSerials` is intentionally read via the closure; the
    // effect re-runs only on content changes (`serialsKey`), `skip`,
    // `apollo`, or an explicit `refetch()` (`retryToken`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialsKey, skip, apollo, retryToken]);

  // Derive `loading` from results coverage. True whenever any requested
  // serial doesn't have an entry in `results` yet — covers both the
  // pre-fetch frame (right after `serials` becomes non-empty, before the
  // effect commits) and the in-flight window. Forced false on `error`
  // so the UI shows the retry path instead of an indefinite spinner.
  const loading = useMemo(() => {
    if (skip || uppercasedSerials.length === 0 || error) {
      return false;
    }
    for (const serial of uppercasedSerials) {
      if (!results.has(serial)) {
        return true;
      }
    }
    return false;
  }, [skip, uppercasedSerials, results, error]);

  const refetch = useCallback(() => {
    setRetryToken((t) => t + 1);
  }, []);

  return { results, loading, error, refetch };
}
